import type { SnapshotComparisonDto, SnapshotUserChangeDto, SnapshotUserReferenceDto } from '@/libs/api-user-mgt/generated/model';
import type { SnapshotCompareChangeKindWire, SnapshotCompareUserRowWire } from '@/userManagement/application/types/SnapshotWire';

/**
 * UM controllers return either a bare DTO (`Ok(value)`) or a serialized `Result<T>` envelope (`Ok(result)`).
 */
export function unwrapSnapshotComparisonResponseBody(data: unknown): SnapshotComparisonDto | null {
  if (data === null || typeof data !== 'object') {
    return null;
  }
  const o = data as Record<string, unknown>;
  const success = o.isSuccess ?? o.IsSuccess;
  if (success === false) {
    return null;
  }
  if (success === true) {
    const inner = o.value ?? o.Value;
    if (inner !== null && typeof inner === 'object') {
      return inner as SnapshotComparisonDto;
    }
    return null;
  }
  if (typeof o.snapshotId === 'number' || typeof o.SnapshotId === 'number') {
    return data as SnapshotComparisonDto;
  }
  return null;
}

function readWhenChanged(source: { whenChanged?: string | null }): string | undefined {
  const raw = source.whenChanged;
  if (typeof raw !== 'string') {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed === '' ? undefined : trimmed;
}

function mapChangeToRow(c: SnapshotUserChangeDto): SnapshotCompareUserRowWire {
  const userId = String(c.crossUserId ?? '').trim();
  const username = String(c.username ?? '').trim();
  const displayName = String(c.fullName ?? '').trim() || username || userId;
  const changeCount = typeof c.changeCount === 'number' && Number.isFinite(c.changeCount) ? c.changeCount : 0;
  const rawApproval = c.inProgressApprovalChangeCount;
  const inProgressApprovalChangeCount =
    typeof rawApproval === 'number' && Number.isFinite(rawApproval) && rawApproval >= 0 ? Math.floor(rawApproval) : undefined;
  const whenChanged = readWhenChanged(c);
  const row: SnapshotCompareUserRowWire = {
    userId,
    username,
    displayName,
    changeCount,
    changeKind: 'Changed'
  };
  if (whenChanged !== undefined) {
    row.whenChanged = whenChanged;
  }
  if (inProgressApprovalChangeCount !== undefined) {
    row.inProgressApprovalChangeCount = inProgressApprovalChangeCount;
  }
  return row;
}

function mapRefToRow(r: SnapshotUserReferenceDto, changeKind: SnapshotCompareChangeKindWire): SnapshotCompareUserRowWire {
  const userId = String(r.crossUserId ?? '').trim();
  const username = String(r.username ?? '').trim();
  const displayName = String(r.fullName ?? '').trim() || username || userId;
  const whenChanged = readWhenChanged(r);
  const row: SnapshotCompareUserRowWire = {
    userId,
    username,
    displayName,
    changeCount: 0,
    changeKind
  };
  if (whenChanged !== undefined) {
    row.whenChanged = whenChanged;
  }
  return row;
}

function dedupeByUserId(rows: SnapshotCompareUserRowWire[]): SnapshotCompareUserRowWire[] {
  const seen = new Set<string>();
  const out: SnapshotCompareUserRowWire[] = [];
  for (const row of rows) {
    const k = row.userId.trim().toLowerCase();
    if (k === '' || seen.has(k)) continue;
    seen.add(k);
    out.push(row);
  }
  return out;
}

function matchesSearch(row: SnapshotCompareUserRowWire, q: string): boolean {
  return row.displayName.toLowerCase().includes(q) || row.username.toLowerCase().includes(q) || row.userId.toLowerCase().includes(q);
}

/**
 * Builds compare-user grid rows from `GET .../permission-snapshots/{id}/comparison`.
 * Includes added, changed, and removed users. When `changesOnly` is true, keeps Added/Removed
 * and Changed rows with `changeCount > 0` (excludes zero-change noise in the changed bucket).
 */
export function buildCompareUserRowsFromComparisonDto(
  dto: SnapshotComparisonDto,
  query?: { q?: string; changesOnly?: boolean }
): SnapshotCompareUserRowWire[] {
  const changed = (dto.changedUsers ?? []).map(mapChangeToRow);
  const added = (dto.addedUsers ?? []).map((r) => mapRefToRow(r, 'Added'));
  const removed = (dto.removedUsers ?? []).map((r) => mapRefToRow(r, 'Removed'));
  // Prefer Changed over Added/Removed when the same user appears in multiple buckets.
  let rows = dedupeByUserId([...changed, ...added, ...removed]);

  const q = query?.q?.trim().toLowerCase() ?? '';
  if (q !== '') {
    rows = rows.filter((r) => matchesSearch(r, q));
  }
  if (query?.changesOnly) {
    rows = rows.filter((r) => r.changeKind === 'Added' || r.changeKind === 'Removed' || r.changeCount > 0);
  }
  return rows;
}
